/**
 * Role helpers for a user object that may carry either shape.
 *
 * The server now returns `roles` (an array) alongside the legacy `role`
 * string, because users.role is a single column and cannot express a contestant
 * who is also a judge. Anyone already logged in has a `user` cached in
 * localStorage from before this change, with no `roles` key at all — so every
 * read must fall back to `role`, or the whole site logs itself out on deploy.
 */
export function userRoles(user) {
  if (!user) return [];
  if (Array.isArray(user.roles) && user.roles.length > 0) return user.roles;
  return user.role ? [user.role] : [];
}

/** True if the user holds any of `allowed`. Admin is a superuser, as on the server. */
export function hasRole(user, allowed) {
  const roles = userRoles(user);
  if (roles.includes('admin')) return true;
  return roles.some((r) => allowed.includes(r));
}

/**
 * True only for someone who is BOTH a FOOM entrant and a judge — the three
 * people users.role could never describe, and the only ones who need a way to
 * move between the two sides mid-session.
 *
 * Admins are deliberately excluded even when they also judge: admin already
 * reaches every page, so the switcher would be one more thing on screen that
 * changes nothing for them.
 */
export function isEntrantJudge(user) {
  const roles = userRoles(user);
  if (roles.includes('admin')) return false;
  return roles.includes('judge') && roles.includes('contestant');
}

/**
 * True for an admin who is ALSO a judge with songs to score.
 *
 * Distinct from isEntrantJudge, which excludes admins on the grounds that an
 * admin already reaches every page. That is true of PERMISSIONS but not of
 * NAVIGATION: login sends an admin to /admin/dashboard, their assignments live
 * at /judge/dashboard, and nothing on screen joins the two. Corey Stewart spent
 * three days trying to reach 8 assigned songs he already had access to.
 */
export function isAdminJudge(user) {
  const roles = userRoles(user);
  return roles.includes('admin') && roles.includes('judge');
}

/**
 * True for someone who has been granted the results page (foom_viewers,
 * migration 050) on top of another role. A pure viewer account lands on
 * /viewer/results anyway; these people land on their own dashboard and need
 * a way across. Admins are excluded: the Results tab is already theirs.
 */
export function isAlsoViewer(user) {
  const roles = userRoles(user);
  if (roles.includes('admin')) return false;
  return roles.includes('observer') && (roles.includes('judge') || roles.includes('contestant'));
}

/**
 * Which dashboard to land on. Most privileged first, so a contestant who is also
 * a judge lands on the judge dashboard — the contestant side is one click away
 * in the header, and a judge with songs waiting is the more urgent of the two.
 */
export function primaryRole(user) {
  const roles = userRoles(user);
  return ['admin', 'judge', 'contestant', 'observer'].find((r) => roles.includes(r)) ?? null;
}
